// Carga Académica (PUCV, formato Anexo 4; ver $usesAcademicLoad en main.js). Las horas por
// División de tiempo se ingresan semanales y se guardan totales (semanales × semanas de la
// asignatura), igual que el Plan de Estudio. Los totales siguen las reglas de pucvCredits.js.
import {
  PUCV_SEMESTER_WEEKS,
  pucvWeeklyHours,
  pucvWeeks,
  toPucvPedagogicalHours,
} from "@/utils/pucvCredits";

// Mismas claves que PERIODICITY_CHOICES en kl-backend (mesh/models.py)
export const PERIODICITIES = ["weekly", "monthly", "bimonthly", "semiannual"];

// Opciones para un select; "" es "sin periodicidad" y se muestra como "-"
export function periodicityOptions(t) {
  return [
    { value: "", text: "-" },
    ...PERIODICITIES.map((periodicity) => ({
      value: periodicity,
      text: t(`studyPlan.academicLoad.periodicities.${periodicity}`),
    })),
  ];
}

export function periodicityLabel(t, periodicity) {
  return PERIODICITIES.includes(periodicity)
    ? t(`studyPlan.academicLoad.periodicities.${periodicity}`)
    : "-";
}

// { [time_allocation_id]: división } de una asignatura del perfil: las de la Asignatura Base
// si usa sus valores (use_matter_values), si no las propias del perfil.
export function allocationsOfMatter(
  egress_profile_matter,
  matter_time_allocations,
  ep_matter_time_allocations
) {
  const allocations = {};
  if (egress_profile_matter.use_matter_values)
    matter_time_allocations
      .filter((x) => x.matter == egress_profile_matter.matter)
      .forEach((x) => (allocations[x.time_allocation] = x));
  else
    ep_matter_time_allocations
      .filter((x) => x.egress_profile_matter == egress_profile_matter.id)
      .forEach((x) => (allocations[x.time_allocation] = x));
  return allocations;
}

export function weeklyHours(hours, weeks) {
  const total = parseFloat(hours) || 0;
  const week_count = parseFloat(weeks) || 0;
  if (total <= 0 || week_count <= 0) return 0;
  return parseFloat((total / week_count).toFixed(2));
}

// Con 2 decimales y sin llevar a horas enteras: hours es FloatField, y redondear cambiaría
// lo ingresado (2,4 × 18 = 43,2 → 43 se vería como 2,39 semanales).
export function totalHoursFromWeekly(weekly_hours, weeks) {
  const weekly = parseFloat(weekly_hours) || 0;
  if (weekly <= 0) return 0;
  return parseFloat((weekly * (parseFloat(weeks) || 0)).toFixed(2));
}

// Horas presenciales semanales en horas PUCV (35 min): solo divisiones que cuentan para
// créditos, separando pedagógicas (minutos del módulo) y cronológicas (60), como MatterForm.
// allocations: { [time_allocation_id]: MatterTimeAllocation o EgressProfileMatterTimeAllocation }
export function presentialWeeklyHours(
  allocations,
  time_allocations,
  weeks,
  module_minutes
) {
  let pedagogical = 0;
  let chronological = 0;
  time_allocations.forEach((time_allocation) => {
    const allocation = allocations[time_allocation.id];
    if (!allocation || !allocation.counts_towards_credits) return;
    const hours = weeklyHours(allocation.hours, weeks);
    if (time_allocation.uses_module_minutes) pedagogical += hours;
    else chronological += hours;
  });
  const hours = toPucvPedagogicalHours(
    pedagogical,
    chronological,
    module_minutes
  );
  return parseFloat(hours.toFixed(2));
}

// Columnas "Totales Horas" del Anexo 4. Autónomo = c × 4 − presencial: si es negativo la
// asignatura tiene más horas presenciales de las que permiten sus créditos.
// weeks: semanas de la asignatura (0 → 18, ver pucvWeeks); sin él, el semestre de 18.
export function academicLoadTotals(
  pucv_credits,
  presential_weekly,
  weeks = PUCV_SEMESTER_WEEKS
) {
  const dedication_weekly = pucvWeeklyHours(pucv_credits);
  const autonomous_weekly = parseFloat(
    (dedication_weekly - presential_weekly).toFixed(2)
  );
  const week_count = pucvWeeks(weeks);
  const semester = (weekly) => parseFloat((weekly * week_count).toFixed(2));
  return {
    presential_weekly: presential_weekly,
    presential_semester: semester(presential_weekly),
    autonomous_weekly: autonomous_weekly,
    autonomous_semester: semester(autonomous_weekly),
    dedication_weekly: dedication_weekly,
    dedication_semester: semester(dedication_weekly),
  };
}

// Totales del Anexo 4 desde las filas de horas que ya arman MatterForm y la presentación
// del programa (una por División, con time_allocation, uses_module_minutes,
// counts_towards_credits y hours totales de la asignatura).
export function academicLoadTotalsFromHours(
  hour_rows,
  pucv_credits,
  weeks,
  module_minutes
) {
  const allocations = {};
  hour_rows.forEach((row) => (allocations[row.time_allocation] = row));
  const time_allocations = hour_rows.map((row) => ({
    id: row.time_allocation,
    uses_module_minutes: row.uses_module_minutes,
  }));
  const presential_weekly = presentialWeeklyHours(
    allocations,
    time_allocations,
    pucvWeeks(weeks),
    module_minutes
  );
  return academicLoadTotals(pucv_credits, presential_weekly, weeks);
}

// La PUCV no permite más horas presenciales de las que dan los créditos (autónomo < 0).
// Sin créditos ingresados no hay contra qué validar, igual que la alerta de MatterForm.
export function exceedsPucvCredits(pucv_credits, autonomous_weekly) {
  return (parseFloat(pucv_credits) || 0) > 0 && autonomous_weekly < 0;
}

// Regla 40/60 de la PUCV: la docencia presencial no puede pasar del 40% de la dedicación
// (el autónomo es al menos el 60%). Solo para DIRECTIVOS FACULTAD O ESCUELA y DIRECTORES DE
// CARRERA; DIRECTIVOS ACADÉMICOS y superuser pueden pasarse, y el resto de roles sigue solo
// con el tope de autónomo negativo. Quien la llama decide si la institución es PUCV.
export const PUCV_MAX_PRESENTIAL_RATIO = 0.4;
const PRESENTIAL_RATIO_GROUPS = [
  "DIRECTIVOS FACULTAD O ESCUELA",
  "DIRECTORES DE CARRERA",
  "DIRECTORES DE CARRERA (CAMPUS)",
];
const PRESENTIAL_RATIO_EXEMPT_GROUPS = [
  "DIRECTIVOS ACADÉMICOS INSTITUCIONALES",
  "DIRECTIVOS ACADÉMICOS (CAMPUS)",
];

export function limitsPresentialRatio(user) {
  if (!user || user.is_superuser) return false;
  const group_names = user.group_names || [];
  if (PRESENTIAL_RATIO_EXEMPT_GROUPS.some((x) => group_names.includes(x)))
    return false;
  return PRESENTIAL_RATIO_GROUPS.some((x) => group_names.includes(x));
}

// Máximo de horas presenciales semanales: toda la dedicación, o el 40% con la regla 40/60
export function maxPresentialWeekly(pucv_credits, limits_ratio = false) {
  const dedication = pucvWeeklyHours(pucv_credits);
  const max = limits_ratio
    ? dedication * PUCV_MAX_PRESENTIAL_RATIO
    : dedication;
  return parseFloat(max.toFixed(2));
}

// Si las horas presenciales pasan del máximo. Sin créditos no hay contra qué validar.
export function exceedsPresentialLimit(
  pucv_credits,
  presential_weekly,
  limits_ratio = false
) {
  return (
    (parseFloat(pucv_credits) || 0) > 0 &&
    presential_weekly > maxPresentialWeekly(pucv_credits, limits_ratio)
  );
}

// Si un cambio debe rechazarse: deja las horas presenciales sobre el máximo y además lo
// empeora. Si ya estaban sobre el máximo, se permiten los cambios que lo acercan para
// poder corregirlo.
export function worsensPucvAutonomous(
  current_credits,
  current_presential,
  next_credits,
  next_presential,
  limits_ratio = false
) {
  const excess = (credits, presential) =>
    presential - maxPresentialWeekly(credits, limits_ratio);
  return (
    exceedsPresentialLimit(next_credits, next_presential, limits_ratio) &&
    excess(next_credits, next_presential) >
      excess(current_credits, current_presential)
  );
}

// Aviso para un cambio rechazado o un guardado bloqueado
export function presentialLimitText(
  t,
  pucv_credits,
  presential_weekly,
  limits_ratio = false
) {
  const values = {
    presential: presential_weekly,
    weekly: pucvWeeklyHours(pucv_credits),
    credits: pucv_credits,
    max: maxPresentialWeekly(pucv_credits, true),
  };
  // Si también deja el autónomo negativo, se avisa eso, que aplica a todos los roles
  if (limits_ratio && presential_weekly <= values.weekly)
    return t("studyPlan.academicLoad.presentialOverRatio", values);
  return t("studyPlan.academicLoad.autonomousNegative", values);
}
